// @flow
import React, { Component } from 'react';
import {
  TextArea, Checkbox, Radio, RadioGroup, Select, Option,
} from 'informed';
import NumericText from '../../../NumericText.jsx';

import { PrimaryText, SecondaryText } from '../../../Typography.jsx';
import ContentBox from '../../../ContentBox.jsx';
import { Button } from '../../../Buttons.jsx';

import styles from '../../../../styles/patientPages/progressNote/progressNote.scss';

type Props = {
  initRespiratory: Object,
  resetOxygenationRadios: Function,
  resetSuctioningRadios: Function,
  resetGagReflexRadios: Function,
  resetCoughRadios: Function,
  resetProductiveCoughRadios: Function,
}

type State = {
  oxygenDisabled: boolean,
}

const publicUrl = process.env.PUBLIC_URL || '';

class EditRespiratory extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      oxygenDisabled: !this.props.initRespiratory.oxygenInUse,
    };
  }

  handleOxygenChange = () => {
    this.setState(prevState => ({
      ...prevState,
      oxygenDisabled: !prevState.oxygenDisabled,
    }));
  }

  render() {
    const respiratory = this.props.initRespiratory || {};
    return (
      <ContentBox title='Respiratory'>
        <div className={styles.col}>
          <PrimaryText>Respiratory Assessment</PrimaryText>
          <div className={styles.section}>
            <SecondaryText>Respiratory pattern (mark all that apply):</SecondaryText><br />
            <Checkbox field='respiratory.even' initialValue={respiratory.even} />Even<br />
            <Checkbox field='respiratory.uneven' initialValue={respiratory.uneven} />Uneven<br />
            <Checkbox field='respiratory.effortless' initialValue={respiratory.effortless} />Effortless<br />
            <Checkbox field='respiratory.labored' initialValue={respiratory.labored} />Labored<br />
            <Checkbox field='respiratory.deep' initialValue={respiratory.deep} />Deep<br />
            <Checkbox field='respiratory.shallow' initialValue={respiratory.shallow} />Shallow<br />
            <Checkbox field='respiratory.gasping' initialValue={respiratory.gasping} />Gasping<br />
            <Checkbox field='respiratory.grunting' initialValue={respiratory.grunting} />Grunting<br />
            <Checkbox field='respiratory.stridor' initialValue={respiratory.stridor} />Stridor<br />
            <Checkbox field='respiratory.tachypneic' initialValue={respiratory.tachypneic} />Tachypneic<br />
            <Checkbox field='respiratory.bradypneic' initialValue={respiratory.bradypneic} />Bradypneic
          </div>
          <div className={styles.section}>
            <SecondaryText>Respiratory effort (mark all that apply):</SecondaryText><br />
            <Checkbox field='respiratory.dyspnea' initialValue={respiratory.dyspnea} />Dyspnea/shortness of breath<br />
            <Checkbox field='respiratory.shortBreathOnExert' initialValue={respiratory.shortBreathOnExert} />Shortness of breath on exertion<br />
            <Checkbox field='respiratory.chestExpansion' initialValue={respiratory.chestExpansion} />Increased chest expansion<br />
            <Checkbox field='respiratory.orthopnea' initialValue={respiratory.orthopnea} />Orthopnea<br />
            <Checkbox field='respiratory.useAccessory' initialValue={respiratory.useAccessory} />Use of accessory muscles<br />
            <Checkbox field='respiratory.retractions' initialValue={respiratory.retractions} />Retractions
          </div>
          <PrimaryText>Lung Auscultation</PrimaryText>
          <div className={styles.section}>
            <img src={`${publicUrl}/sa_assets/lungs.png`} alt='lungs' width="400px" />
            <table>
              <tbody>
                <tr>
                  <td>Upper Left Anterior</td>
                  <td>
                    <Select field='respiratory.upperLeftAnteriorAuscultation' initialValue={respiratory.upperLeftAnteriorAuscultation}>
                      <Option value=''>Select Value...</Option>
                      <Option value='clear'>Clear</Option>
                      <Option value='fineCrackles'>Fine crackles</Option>
                      <Option value='courseCrackles'>Course crackles</Option>
                      <Option value='rhonchi'>Ronchi</Option>
                      <Option value='rales'>Rales</Option>
                      <Option value='wheeze'>Wheeze</Option>
                      <Option value='diminished'>Diminished</Option>
                      <Option value='absent'>Absent</Option>
                    </Select>
                  </td>
                  <td>
                    <Select field='respiratory.upperLeftAnteriorWheeze' initialValue={respiratory.upperLeftAnteriorWheeze}>
                      <Option value=''>Wheeze Description...</Option>
                      <Option value='inspiratory'>Inspiratory</Option>
                      <Option value='expiratory'>Expiratory</Option>
                      <Option value='throughout'>Throughout</Option>
                    </Select>
                  </td>
                </tr>
                <tr>
                  <td>Upper Right Anterior</td>
                  <td>
                    <Select field='respiratory.upperRightAnteriorAuscultation' initialValue={respiratory.upperRightAnteriorAuscultation}>
                      <Option value=''>Select Value...</Option>
                      <Option value='clear'>Clear</Option>
                      <Option value='fineCrackles'>Fine crackles</Option>
                      <Option value='courseCrackles'>Course crackles</Option>
                      <Option value='rhonchi'>Ronchi</Option>
                      <Option value='rales'>Rales</Option>
                      <Option value='wheeze'>Wheeze</Option>
                      <Option value='diminished'>Diminished</Option>
                      <Option value='absent'>Absent</Option>
                    </Select>
                  </td>
                  <td>
                    <Select field='respiratory.upperRightAnteriorWheeze' initialValue={respiratory.upperRightAnteriorWheeze}>
                      <Option value=''>Wheeze Description...</Option>
                      <Option value='inspiratory'>Inspiratory</Option>
                      <Option value='expiratory'>Expiratory</Option>
                      <Option value='throughout'>Throughout</Option>
                    </Select>
                  </td>
                </tr>
                <tr>
                  <td>Left Lateral</td>
                  <td>
                    <Select field='respiratory.leftLateralAuscultation' initialValue={respiratory.leftLateralAuscultation}>
                      <Option value=''>Select Value...</Option>
                      <Option value='clear'>Clear</Option>
                      <Option value='fineCrackles'>Fine crackles</Option>
                      <Option value='courseCrackles'>Course crackles</Option>
                      <Option value='rhonchi'>Ronchi</Option>
                      <Option value='rales'>Rales</Option>
                      <Option value='wheeze'>Wheeze</Option>
                      <Option value='diminished'>Diminished</Option>
                      <Option value='absent'>Absent</Option>
                    </Select>
                  </td>
                  <td>
                    <Select field='respiratory.leftLateralWheeze' initialValue={respiratory.leftLateralWheeze}>
                      <Option value=''>Wheeze Description...</Option>
                      <Option value='inspiratory'>Inspiratory</Option>
                      <Option value='expiratory'>Expiratory</Option>
                      <Option value='throughout'>Throughout</Option>
                    </Select>
                  </td>
                </tr>
                <tr>
                  <td>Right Lateral</td>
                  <td>
                    <Select field='respiratory.rightLateralAuscultation' initialValue={respiratory.rightLateralAuscultation}>
                      <Option value=''>Select Value...</Option>
                      <Option value='clear'>Clear</Option>
                      <Option value='fineCrackles'>Fine crackles</Option>
                      <Option value='courseCrackles'>Course crackles</Option>
                      <Option value='rhonchi'>Ronchi</Option>
                      <Option value='rales'>Rales</Option>
                      <Option value='wheeze'>Wheeze</Option>
                      <Option value='diminished'>Diminished</Option>
                      <Option value='absent'>Absent</Option>
                    </Select>
                  </td>
                  <td>
                    <Select field='respiratory.rightLateralWheeze' initialValue={respiratory.rightLateralWheeze}>
                      <Option value=''>Wheeze Description...</Option>
                      <Option value='inspiratory'>Inspiratory</Option>
                      <Option value='expiratory'>Expiratory</Option>
                      <Option value='throughout'>Throughout</Option>
                    </Select>
                  </td>
                </tr>
                <tr>
                  <td>Upper Left Posterior</td>
                  <td>
                    <Select field='respiratory.upperLeftPosteriorAuscultation' initialValue={respiratory.upperLeftPosteriorAuscultation}>
                      <Option value=''>Select Value...</Option>
                      <Option value='clear'>Clear</Option>
                      <Option value='fineCrackles'>Fine crackles</Option>
                      <Option value='courseCrackles'>Course crackles</Option>
                      <Option value='rhonchi'>Ronchi</Option>
                      <Option value='rales'>Rales</Option>
                      <Option value='wheeze'>Wheeze</Option>
                      <Option value='diminished'>Diminished</Option>
                      <Option value='absent'>Absent</Option>
                    </Select>
                  </td>
                  <td>
                    <Select field='respiratory.upperLeftPosteriorWheeze' initialValue={respiratory.upperLeftPosteriorWheeze}>
                      <Option value=''>Wheeze Description...</Option>
                      <Option value='inspiratory'>Inspiratory</Option>
                      <Option value='expiratory'>Expiratory</Option>
                      <Option value='throughout'>Throughout</Option>
                    </Select>
                  </td>
                </tr>
                <tr>
                  <td>Upper Right Posterior</td>
                  <td>
                    <Select field='respiratory.upperRightPosteriorAuscultation' initialValue={respiratory.upperRightPosteriorAuscultation}>
                      <Option value=''>Select Value...</Option>
                      <Option value='clear'>Clear</Option>
                      <Option value='fineCrackles'>Fine crackles</Option>
                      <Option value='courseCrackles'>Course crackles</Option>
                      <Option value='rhonchi'>Ronchi</Option>
                      <Option value='rales'>Rales</Option>
                      <Option value='wheeze'>Wheeze</Option>
                      <Option value='diminished'>Diminished</Option>
                      <Option value='absent'>Absent</Option>
                    </Select>
                  </td>
                  <td>
                    <Select field='respiratory.upperRightPosteriorWheeze' initialValue={respiratory.upperRightPosteriorWheeze}>
                      <Option value=''>Wheeze Description...</Option>
                      <Option value='inspiratory'>Inspiratory</Option>
                      <Option value='expiratory'>Expiratory</Option>
                      <Option value='throughout'>Throughout</Option>
                    </Select>
                  </td>
                </tr>
                <tr>
                  <td>Lower Left Posterior</td>
                  <td>
                    <Select field='respiratory.lowerLeftPosteriorAuscultation' initialValue={respiratory.lowerLeftPosteriorAuscultation}>
                      <Option value=''>Select Value...</Option>
                      <Option value='clear'>Clear</Option>
                      <Option value='fineCrackles'>Fine crackles</Option>
                      <Option value='courseCrackles'>Course crackles</Option>
                      <Option value='rhonchi'>Ronchi</Option>
                      <Option value='rales'>Rales</Option>
                      <Option value='wheeze'>Wheeze</Option>
                      <Option value='diminished'>Diminished</Option>
                      <Option value='absent'>Absent</Option>
                    </Select>
                  </td>
                  <td>
                    <Select field='respiratory.lowerLeftPosteriorWheeze' initialValue={respiratory.lowerLeftPosteriorWheeze}>
                      <Option value=''>Wheeze Description...</Option>
                      <Option value='inspiratory'>Inspiratory</Option>
                      <Option value='expiratory'>Expiratory</Option>
                      <Option value='throughout'>Throughout</Option>
                    </Select>
                  </td>
                </tr>
                <tr>
                  <td>Lower Right Posterior</td>
                  <td>
                    <Select field='respiratory.lowerRightPosteriorAuscultation' initialValue={respiratory.lowerRightPosteriorAuscultation}>
                      <Option value=''>Select Value...</Option>
                      <Option value='clear'>Clear</Option>
                      <Option value='fineCrackles'>Fine crackles</Option>
                      <Option value='courseCrackles'>Course crackles</Option>
                      <Option value='rhonchi'>Ronchi</Option>
                      <Option value='rales'>Rales</Option>
                      <Option value='wheeze'>Wheeze</Option>
                      <Option value='diminished'>Diminished</Option>
                      <Option value='absent'>Absent</Option>
                    </Select>
                  </td>
                  <td>
                    <Select field='respiratory.lowerRightPosteriorWheeze' initialValue={respiratory.lowerRightPosteriorWheeze}>
                      <Option value=''>Wheeze Description...</Option>
                      <Option value='inspiratory'>Inspiratory</Option>
                      <Option value='expiratory'>Expiratory</Option>
                      <Option value='throughout'>Throughout</Option>
                    </Select>
                  </td>
                </tr>
              </tbody>
            </table>
            <div>
              <SecondaryText>Respiratory pattern and breath sounds notes:</SecondaryText>
              <TextArea field='respiratory.lungAuscultationNotes' initialValue={respiratory.lungAuscultationNotes} />
            </div>
          </div>
          <PrimaryText>Oxygenation</PrimaryText>
          <Button onClick={this.props.resetOxygenationRadios}>Clear Radios</Button>
          <div className={styles.section}>
            <Checkbox field='respiratory.oxygenInUse' initialValue={respiratory.oxygenInUse} onChange={this.handleOxygenChange} />Oxygen in use<br />
            <RadioGroup field='respiratory.oxygenAmount' initialValue={respiratory.oxygenAmount}>
              <Radio value='oxyL/min' disabled={this.state.oxygenDisabled} />
              Amount of oxygen in L/min: <NumericText field='respiratory.oxygenLPerMin' disabled={this.state.oxygenDisabled} className={this.state.oxygenDisabled ? styles.disabledText : ''} initialValue={respiratory.oxygenLPerMin} size={4} /><br />
              <Radio value='oxy%' disabled={this.state.oxygenDisabled} />
              Amount of oxygen in %: <NumericText field='respiratory.oxygenPercent' disabled={this.state.oxygenDisabled} className={this.state.oxygenDisabled ? styles.disabledText : ''} initialValue={respiratory.oxygenPercent} size={4} /><br />
            </RadioGroup>
          </div>
          <div className={styles.section}>
            <SecondaryText>Oxygen delivery system:</SecondaryText><br />
            <RadioGroup field='respiratory.oxygenDelivery' initialValue={respiratory.oxygenDelivery}>
              <Radio value='room air' />Room air<br />
              <Radio value='aerosol' />Aerosol<br />
              <Radio value='nasal cannula' />Nasal Cannula<br />
              <Radio value='non-rebreather mask' />Non-rebreather Mask<br />
              <Radio value='partial rebreather mask' />Partial Rebreather Mask<br />
              <Radio value='simple mask' />Simple Mask<br />
              <Radio value='venturi mask' />Venturi mask<br />
              <Radio value='other' />Other
            </RadioGroup>
          </div>
          <div className={styles.section}>
            <SecondaryText>Oxygenation notes:</SecondaryText><br />
            <TextArea field='respiratory.oxygenationNotes' initialValue={respiratory.oxygenationNotes} />
          </div>
          <div className={styles.section}>
            <SecondaryText>Respiratory/breathing support (mark all that apply):</SecondaryText><br />
            <Checkbox field='respiratory.nebulizer' initialValue={respiratory.nebulizer} />Nebulizer treatment<br />
            <Checkbox field='respiratory.incentiveSpiro' initialValue={respiratory.incentiveSpiro} />Incentive spirometer<br />
            Incentive spirometer best effort: <NumericText field='respiratory.incentiveSprioBest' initialValue={respiratory.incentiveSpiroBest} size={4} /> mL
          </div>
          <PrimaryText>Suctioning</PrimaryText><br />
          <Button onClick={this.props.resetSuctioningRadios}>Clear Radios</Button>
          <div className={styles.section}>
            <Checkbox field='respiratory.periodic' initialvalue={respiratory.periodic} />Periodic<br />
            <Checkbox field='respiratory.frequentSuction' initialValue={respiratory.frequentSuction} />Frequent suctioning with bulb syringe<br />
            <SecondaryText>Amount:</SecondaryText><br />
            <RadioGroup field='respiratory.suctionAmount' initialValue={respiratory.suctionAmount}>
              <Radio value='scant' />Scant<br />
              <Radio value='moderate' />Moderate<br />
              <Radio value='copious' />Copious<br />
              <Radio value='other' />Other
            </RadioGroup>
            <br />
            <SecondaryText>Color:</SecondaryText><br />
            <RadioGroup field='respiratory.suctionColor' initialValue={respiratory.suctionColor}>
              <Radio value='clear' />Clear<br />
              <Radio value='white' />White<br />
              <Radio value='yellow' />Yellow<br />
              <Radio value='dark yellow' />Dark Yellow<br />
              <Radio value='tan' />Tan<br />
              <Radio value='brown' />Brown<br />
              <Radio value='green' />Green<br />
              <Radio value='blood-tinged' />Blood-tinged<br />
              <Radio value='blood' />Blood<br />
              <Radio value='other' />Other
            </RadioGroup>
            <br />
            <SecondaryText>Consistency:</SecondaryText><br />
            <RadioGroup field='respiratory.suctionConsistency' initialValue={respiratory.suctionConsistency}>
              <Radio value='thin' />Thin<br />
              <Radio value='frothy' />Frothy<br />
              <Radio value='thick' />Thick<br />
              <Radio value='other' />Other
            </RadioGroup>
            <br />
            <SecondaryText>Odor:</SecondaryText><br />
            <RadioGroup field='respiratory.suctionOdor' initialValue={respiratory.suctionOdor}>
              <Radio value='none' />None<br />
              <Radio value='foul' />Foul<br />
              <Radio value='other' />Other
            </RadioGroup>
            <br />
            Other Suctioning Notes:<br />
            <TextArea field='respiratory.otherSuctionNotes' initialValue={respiratory.otherSuctionNotes} />
          </div>
          <PrimaryText>Gag Reflex</PrimaryText><br />
          <Button onClick={this.props.resetGagReflexRadios}>Clear Radios</Button>
          <div className={styles.section}>
            <table>
              <tbody className={styles.topAlign}>
                <tr>
                  <td><SecondaryText>Gag reflex:</SecondaryText></td>
                  <td>
                    <RadioGroup field='respiratory.gagReflex' initialValue={respiratory.gagReflex}>
                      <Radio value='present' />Present
                      <br />
                      <Radio value='not present' />Not present
                    </RadioGroup>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <PrimaryText>Cough</PrimaryText><br />
          <Button onClick={this.props.resetCoughRadios}>Clear Radios</Button>
          <div className={styles.section}>
            <table>
              <tbody className={styles.topAlign}>
                <tr>
                  <td><SecondaryText>Cough type:</SecondaryText></td>
                  <td>
                    <RadioGroup field='respiratory.coughType' initialValue={respiratory.coughType}>
                      <Radio value='none' />None<br />
                      <Radio value='productive' />Productive<br />
                      <Radio value='nonproductive' />Nonproductive<br />
                      <Radio value='dry' />Dry<br />
                      <Radio value='wet' />Wet
                    </RadioGroup>
                  </td>
                </tr>
                <tr>
                  <td><SecondaryText>Cough strength:</SecondaryText></td>
                  <td>
                    <RadioGroup field='respiratory.coughStrength' initialValue={respiratory.coughStrength}>
                      <Radio value='strong' />Strong<br />
                      <Radio value='weak' />Weak
                    </RadioGroup>
                  </td>
                </tr>
              </tbody>
            </table>
            <SecondaryText>Cough notes:</SecondaryText><br />
            <TextArea field='respiratory.coughNotes' initialValue={respiratory.coughNotes} />
          </div>
          <PrimaryText>Productive Cough Secretions Assessment</PrimaryText><br />
          <Button onClick={this.props.resetProductiveCoughRadios}>Clear Radios</Button>
          <div className={styles.section}>
            <SecondaryText>Amount:</SecondaryText><br />
            <RadioGroup field='respiratory.coughAmount' initialValue={respiratory.coughAmount}>
              <Radio value='scant' />Scant<br />
              <Radio value='moderate' />Moderate<br />
              <Radio value='copious' />Copious<br />
              <Radio value='other' />Other
            </RadioGroup>
            <br />
            <SecondaryText>Color:</SecondaryText><br />
            <RadioGroup field='respiratory.coughColor' initialValue={respiratory.coughColor}>
              <Radio value='clear' />Clear<br />
              <Radio value='white' />White<br />
              <Radio value='yellow' />Yellow<br />
              <Radio value='dark yellow' />Dark Yellow<br />
              <Radio value='tan' />Tan<br />
              <Radio value='brown' />Brown<br />
              <Radio value='green' />Green<br />
              <Radio value='blood-tinged' />Blood-tinged<br />
              <Radio value='blood' />Blood<br />
              <Radio value='other' />Other
            </RadioGroup>
            <br />
            <SecondaryText>Consistency:</SecondaryText><br />
            <RadioGroup field='respiratory.coughConsistency' initialValue={respiratory.coughConsistency}>
              <Radio value='thin' />Thin<br />
              <Radio value='frothy' />Frothy<br />
              <Radio value='thick' />Thick<br />
              <Radio value='other' />Other
            </RadioGroup>
            <br />
            <SecondaryText>Odor:</SecondaryText><br />
            <RadioGroup field='respiratory.coughOdor' initialValue={respiratory.coughOdor}>
              <Radio value='none' />None<br />
              <Radio value='foul' />Foul<br />
              <Radio value='other' />Other
            </RadioGroup>
            <br />
            Other Cough Secretions Notes:<br />
            <TextArea field='respiratory.otherCoughNotes' initialValue={respiratory.otherCoughNotes} />
          </div>
        </div>
      </ContentBox>
    );
  }
}

export default EditRespiratory;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/progressNote/edit/EditRespiratory.jsx